import type { LocalIfcQualityIssue } from "./local-ifc-projection";
import type { FixtureElement } from "./types";

export type QualitySeverity = "error" | "warning" | "info" | "unknown";

export interface QualityIssueView {
  readonly id: string;
  readonly source: LocalIfcQualityIssue["source"];
  readonly severity: QualitySeverity;
  readonly rule: string;
  readonly message: string;
  readonly globalIds: readonly string[];
  readonly matchedElementIds: readonly string[];
  readonly linkage: "linked" | "unlinked";
  readonly raw: Readonly<Record<string, unknown>>;
}

export interface QualitySummary {
  readonly state: "partial";
  readonly total: number;
  readonly severityCounts: Readonly<Record<QualitySeverity, number>>;
  readonly ruleCounts: readonly { readonly rule: string; readonly count: number }[];
  readonly linkedCount: number;
  readonly unlinkedCount: number;
}

interface QualityContext {
  readonly issues: readonly LocalIfcQualityIssue[];
  readonly elementIdsByGlobalId: ReadonlyMap<string, readonly string[]>;
}

const qualityContexts = new WeakMap<QualitySummary, QualityContext>();

export interface MissingPropertyPlan {
  readonly property: string;
  readonly selectedCount: number;
  readonly eligibleElementIds: readonly string[];
  readonly alreadyPresentElementIds: readonly string[];
  readonly refusedElementIds: readonly string[];
}

function normalizeSeverity(value: string): QualitySeverity {
  const normalized = value.toLowerCase();
  return normalized === "error" || normalized === "warning" || normalized === "info" ? normalized : "unknown";
}

function issueRule(issue: LocalIfcQualityIssue): string {
  const candidate = issue.source === "geometry" ? issue.raw.code : issue.raw.rule;
  return typeof candidate === "string" && candidate.trim() !== "" ? candidate : `${issue.source}:unclassified`;
}

function issueGlobalIds(issue: LocalIfcQualityIssue): readonly string[] {
  if (!Array.isArray(issue.raw.globalIds)) return [];
  return [...new Set(issue.raw.globalIds.filter((value): value is string => typeof value === "string" && value !== ""))].sort();
}

export function summarizeQualityIssues(
  issues: readonly LocalIfcQualityIssue[],
  elements: readonly FixtureElement[]
): QualitySummary {
  const elementIdsByGlobalId = new Map<string, string[]>();
  for (const element of elements) {
    const globalId = element.properties.GlobalId;
    if (typeof globalId !== "string" || globalId === "") continue;
    const ids = elementIdsByGlobalId.get(globalId) ?? [];
    ids.push(element.id);
    elementIdsByGlobalId.set(globalId, ids);
  }

  const severityCounts: Record<QualitySeverity, number> = { error: 0, warning: 0, info: 0, unknown: 0 };
  const ruleCounts = new Map<string, number>();
  let linkedCount = 0;
  for (const issue of issues) {
    const severity = normalizeSeverity(issue.severity);
    const rule = issueRule(issue);
    severityCounts[severity] += 1;
    ruleCounts.set(rule, (ruleCounts.get(rule) ?? 0) + 1);
    if (issueGlobalIds(issue).some((globalId) => elementIdsByGlobalId.has(globalId))) linkedCount += 1;
  }
  const summary: QualitySummary = {
    state: "partial",
    total: issues.length,
    severityCounts,
    ruleCounts: [...ruleCounts].map(([rule, count]) => ({ rule, count })).sort((left, right) => left.rule.localeCompare(right.rule)),
    linkedCount,
    unlinkedCount: issues.length - linkedCount
  };
  qualityContexts.set(summary, { issues, elementIdsByGlobalId });
  return summary;
}

export function qualityIssuePage(
  summary: QualitySummary,
  severity: QualitySeverity | "all",
  limit = 200
): readonly QualityIssueView[] {
  if (!Number.isSafeInteger(limit) || limit < 0) throw new RangeError("Quality issue page limit must be a non-negative safe integer.");
  const context = qualityContexts.get(summary);
  if (context === undefined || limit === 0) return [];
  const views: QualityIssueView[] = [];
  for (const [index, issue] of context.issues.entries()) {
    const normalizedSeverity = normalizeSeverity(issue.severity);
    if (severity !== "all" && normalizedSeverity !== severity) continue;
    const globalIds = issueGlobalIds(issue);
    const matchedElementIds = [...new Set(globalIds.flatMap((globalId) => context.elementIdsByGlobalId.get(globalId) ?? []))].sort();
    views.push({
      id: `${issue.source}-${String(index + 1).padStart(5, "0")}`,
      source: issue.source,
      severity: normalizedSeverity,
      rule: issueRule(issue),
      message: issue.message,
      globalIds,
      matchedElementIds,
      linkage: matchedElementIds.length > 0 ? "linked" : "unlinked",
      raw: issue.raw
    });
    if (views.length === limit) break;
  }
  return views;
}

export function planMissingPropertyDraft(
  elements: readonly FixtureElement[],
  selectedElementIds: ReadonlySet<string>,
  property: string
): MissingPropertyPlan {
  const normalizedProperty = property.trim();
  if (normalizedProperty === "") throw new RangeError("Missing-property planning requires a non-empty property name.");
  if (selectedElementIds.size === 0) throw new RangeError("Select at least one element before planning a missing-property draft.");
  const elementsById = new Map(elements.map((element) => [element.id, element]));
  const unknown = [...selectedElementIds].filter((id) => !elementsById.has(id)).sort();
  if (unknown.length > 0) throw new RangeError(`Selected element is not in the inspection dataset: ${unknown.join(", ")}`);

  const eligibleElementIds: string[] = [];
  const alreadyPresentElementIds: string[] = [];
  const refusedElementIds: string[] = [];
  for (const id of [...selectedElementIds].sort()) {
    const element = elementsById.get(id)!;
    if (Object.prototype.hasOwnProperty.call(element.properties, normalizedProperty)) alreadyPresentElementIds.push(id);
    else if (!element.editable || element.unsupportedReason !== undefined) refusedElementIds.push(id);
    else eligibleElementIds.push(id);
  }
  return {
    property: normalizedProperty,
    selectedCount: selectedElementIds.size,
    eligibleElementIds,
    alreadyPresentElementIds,
    refusedElementIds
  };
}
